import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import TrustFilieBadge from '../shared/TrustFilieBadge';
import { type FieldSource } from '../shared/TrustFilieBadge';

const filingStatusLabel: Record<string, string> = {
  single: 'Single',
  'married-jointly': 'Married Filing Jointly',
  'married-separately': 'Married Filing Separately',
  'head-of-household': 'Head of Household',
  'qualifying-widow': 'Qualifying Widow(er)',
};

const lifeEventLabels: Record<string, string> = {
  married: 'Got Married',
  divorced: 'Got Divorced',
  'had-baby': 'Had a Baby',
  'bought-home': 'Bought a Home',
  'sold-home': 'Sold a Home',
  'started-business': 'Started a Business',
  'changed-jobs': 'Changed Jobs',
  retired: 'Retired',
  'none-of-above': 'None',
};

const docTypeLabels: Record<string, string> = {
  'W-2': 'W-2',
  '1099-NEC': '1099-NEC',
  '1099-MISC': '1099-MISC',
  '1099-INT': '1099-INT',
  '1099-DIV': '1099-DIV',
  '1099-B': '1099-B',
  '1099-R': '1099-R',
  '1099-G': '1099-G',
  'K-1': 'K-1',
};

const fmt = (v: number | undefined | null): string => {
  if (!v) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
      <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

interface RowProps {
  label: string;
  value: string;
  source?: FieldSource;
  documentType?: string;
}

function Row({ label, value, source, documentType }: RowProps) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-600">{label}</span>
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-gray-900">{value}</span>
        {source && <TrustFilieBadge source={source} documentType={documentType} />}
      </div>
    </div>
  );
}

export default function SmartReviewStep() {
  const { answers, smartDefaults, fieldOverrides } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const incomeSources = smartDefaults?.incomeSources || [];
  const deductions = answers.deductionDetails;
  const payments = answers.taxPayments;
  const dependents = answers.dependents || [];
  const lifeEvents = answers.lifeEvents || [];
  const analyzedCount = smartDefaults?.documentsStatus?.analyzed || 0;

  // Determine sources for review display
  const filingStatusSource: FieldSource = fieldOverrides['filingStatus']
    ? 'user'
    : smartDefaults?.suggestedFilingStatus
      ? 'document'
      : 'user';

  const nameSource: FieldSource = fieldOverrides['name']
    ? 'user'
    : smartDefaults?.primaryFiler?.name
      ? 'document'
      : 'user';

  // Collect trust-filie fields that have no document backing
  const trustFilieFields: string[] = [];
  const adj = deductions?.adjustments;
  if (adj) {
    if (
      adj.hsaContributions === 0 &&
      !smartDefaults?.hsaContributions &&
      !fieldOverrides['adjustment_hsaContributions']
    )
      trustFilieFields.push('HSA Contributions');
    if (
      adj.traditionalIra === 0 &&
      !fieldOverrides['adjustment_traditionalIra']
    )
      trustFilieFields.push('Traditional IRA');
    if (
      adj.studentLoanInterest === 0 &&
      !fieldOverrides['adjustment_studentLoanInterest']
    )
      trustFilieFields.push('Student Loan Interest');
    if (
      adj.educatorExpenses === 0 &&
      !fieldOverrides['adjustment_educatorExpenses']
    )
      trustFilieFields.push('Educator Expenses');
    if (
      adj.seHealthInsurance === 0 &&
      !fieldOverrides['adjustment_seHealthInsurance']
    )
      trustFilieFields.push('SE Health Insurance');
  }

  const estimatedTotal =
    (payments?.estimatedPayments?.q1 || 0) +
    (payments?.estimatedPayments?.q2 || 0) +
    (payments?.estimatedPayments?.q3 || 0) +
    (payments?.estimatedPayments?.q4 || 0);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review & Submit</h2>
        <p className="text-gray-500 mt-1">
          Confirm everything looks correct before we run the numbers
        </p>
      </div>

      {/* Filing Status */}
      <Section title="Filing Status">
        <Row
          label="Status"
          value={
            answers.filingStatus
              ? filingStatusLabel[answers.filingStatus] || answers.filingStatus
              : 'Not selected'
          }
          source={filingStatusSource}
        />
        {smartDefaults?.filingStatusReason && (
          <p className="text-xs text-gray-500 mt-1">
            {smartDefaults.filingStatusReason}
          </p>
        )}
      </Section>

      {/* Personal Information */}
      <Section title="Personal Information">
        {answers.personalInfo?.name && (
          <Row
            label="Name"
            value={answers.personalInfo.name}
            source={nameSource}
          />
        )}
        {answers.personalInfo?.stateOfResidence && (
          <Row
            label="State"
            value={answers.personalInfo.stateOfResidence}
            source={
              fieldOverrides['stateOfResidence']
                ? 'user'
                : smartDefaults?.primaryFiler?.state
                  ? 'document'
                  : 'user'
            }
          />
        )}
        {answers.personalInfo?.occupation && (
          <Row label="Occupation" value={answers.personalInfo.occupation} source="user" />
        )}
        {answers.personalInfo?.spouseInfo?.name && (
          <Row
            label="Spouse"
            value={answers.personalInfo.spouseInfo.name}
            source={
              fieldOverrides['spouseName']
                ? 'user'
                : smartDefaults?.spouseDetected?.name
                  ? 'document'
                  : 'user'
            }
          />
        )}
      </Section>

      {/* Income Sources */}
      <Section title="Income Sources">
        {incomeSources.length > 0 ? (
          <div className="space-y-1">
            {incomeSources.map((source) => {
              const total = source.documents.reduce(
                (sum, doc) => sum + doc.amount,
                0
              );
              return (
                <div key={source.type} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {docTypeLabels[source.type] || source.type}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({source.documents.length} doc
                      {source.documents.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {fmt(total)}
                    </span>
                    <TrustFilieBadge
                      source="document"
                      documentType={source.type}
                    />
                  </div>
                </div>
              );
            })}

            {/* Withholding */}
            {(smartDefaults?.totalWithholding || 0) > 0 && (
              <div className="border-t border-gray-200 mt-2 pt-2">
                <Row
                  label="Federal Withholding"
                  value={fmt(smartDefaults?.totalWithholding)}
                  source="document"
                />
                <Row
                  label="State Withholding"
                  value={fmt(smartDefaults?.stateWithholding)}
                  source="document"
                />
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No income sources detected</p>
        )}
      </Section>

      {/* Deductions */}
      <Section title="Deductions & Adjustments">
        <Row
          label="Preference"
          value={
            deductions?.preference === 'standard'
              ? 'Standard Deduction'
              : deductions?.preference === 'itemized'
                ? 'Itemized Deductions'
                : 'Trust Filie (will recommend)'
          }
          source={fieldOverrides['deductionPreference'] ? 'user' : 'trust-filie'}
        />
        {deductions?.preference === 'itemized' && (
          <>
            {deductions.itemized.mortgageInterest > 0 && (
              <Row label="Mortgage Interest" value={fmt(deductions.itemized.mortgageInterest)} source="user" />
            )}
            {deductions.itemized.stateLocalTaxes > 0 && (
              <Row label="SALT" value={fmt(deductions.itemized.stateLocalTaxes)} source="user" />
            )}
            {deductions.itemized.charitableContributions > 0 && (
              <Row label="Charitable" value={fmt(deductions.itemized.charitableContributions)} source="user" />
            )}
            {deductions.itemized.medicalExpenses > 0 && (
              <Row label="Medical" value={fmt(deductions.itemized.medicalExpenses)} source="user" />
            )}
          </>
        )}
        {adj && adj.hsaContributions > 0 && (
          <Row
            label="HSA Contributions"
            value={fmt(adj.hsaContributions)}
            source={smartDefaults?.hsaContributions ? 'document' : 'user'}
          />
        )}
        {smartDefaults?.retirementContributions && (
          <Row
            label="Retirement (employer)"
            value={fmt(smartDefaults.retirementContributions)}
            source="document"
            documentType="W-2"
          />
        )}
        {adj && adj.traditionalIra > 0 && (
          <Row label="Traditional IRA" value={fmt(adj.traditionalIra)} source="user" />
        )}
        {adj && adj.studentLoanInterest > 0 && (
          <Row label="Student Loan Interest" value={fmt(adj.studentLoanInterest)} source="user" />
        )}
        {adj && adj.educatorExpenses > 0 && (
          <Row label="Educator Expenses" value={fmt(adj.educatorExpenses)} source="user" />
        )}
        {adj && adj.seHealthInsurance > 0 && (
          <Row label="SE Health Insurance" value={fmt(adj.seHealthInsurance)} source="user" />
        )}
      </Section>

      {/* Tax Payments */}
      <Section title="Tax Payments">
        {estimatedTotal > 0 ? (
          <Row
            label="Estimated Payments"
            value={fmt(estimatedTotal)}
            source={smartDefaults?.estimatedPayments ? 'document' : 'user'}
          />
        ) : (
          <p className="text-sm text-gray-500">No estimated payments</p>
        )}
        {(payments?.priorYearOverpayment || 0) > 0 && (
          <Row
            label="Prior Year Overpayment"
            value={fmt(payments?.priorYearOverpayment)}
            source={smartDefaults?.priorYearOverpayment ? 'document' : 'user'}
          />
        )}
      </Section>

      {/* Dependents */}
      <Section title="Dependents">
        {dependents.length > 0 ? (
          <div className="space-y-1">
            {dependents.map((dep) => (
              <div key={dep.id} className="flex items-center justify-between py-1">
                <span className="text-sm text-gray-900">{dep.name}</span>
                <span className="text-xs text-gray-500">{dep.relationship}</span>
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-1">
              {dependents.length} dependent{dependents.length !== 1 ? 's' : ''}
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No dependents</p>
        )}
      </Section>

      {/* Life Events */}
      <Section title="Life Events">
        {lifeEvents.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {lifeEvents.map((event) => (
              <span
                key={event}
                className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700"
              >
                {lifeEventLabels[event] || event}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">None selected</p>
        )}
      </Section>

      {/* Document count */}
      {analyzedCount > 0 && (
        <p className="text-sm text-gray-500 text-center">
          Based on {analyzedCount} analyzed document
          {analyzedCount !== 1 ? 's' : ''}
        </p>
      )}

      {/* Trust Filie notice */}
      {trustFilieFields.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-start space-x-2">
            <TrustFilieBadge source="trust-filie" />
            <p className="text-sm text-gray-600">
              Fields marked "Trust Filie" will use our best recommendation
              during analysis: {trustFilieFields.join(', ')}.
            </p>
          </div>
        </div>
      )}

      {/* Ready box */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 text-center">
        <p className="text-sm text-gray-700">
          Your questionnaire is complete. Click submit to start your tax return
          analysis.
        </p>
      </div>
    </div>
  );
}
